import type {
  AiRoundtripDetail,
  EventId,
  IsoTimestamp,
  ModelHttpRequestDetail,
  ModelHttpRequestSummary,
  PlatformStatus,
  PlayerId,
  RoomId,
  RoundtripId,
  RunId,
  TraceDetail,
  TraceEvent,
  TraceId,
  TraceListItem,
  ViewerIdentity,
} from '@maland/contracts';
import type { AuditEventDto, DashboardData } from '../types';

const baseTime = Date.now();
const ago = (milliseconds: number) => new Date(baseTime - milliseconds).toISOString() as IsoTimestamp;
const opaque = <T extends string>(value: string) => value as T;

const traceId = opaque<TraceId>('trace_01JZNRD86F');
const runId = opaque<RunId>('run_9c1fd83a');
const roomId = opaque<RoomId>('room-raven-04');
const playerId = opaque<PlayerId>('player_1287');

export const mockTraces: TraceListItem[] = [
  {
    traceId,
    runId,
    roomId,
    playerId,
    source: 'ai-backend',
    status: 'error',
    startedAt: ago(92_000),
    completedAt: ago(79_158),
    durationMs: 12_842,
    tokenUsage: { input: 6380, output: 1234, total: 7614 },
    headline: '码头守卫对峙 · 自由行动',
    error: '客户端在动作完成前释放了选项面板',
  },
  {
    traceId: opaque<TraceId>('trace_01JZNR8FWR'),
    runId: opaque<RunId>('run_7b250ee1'),
    roomId,
    playerId: opaque<PlayerId>('player_4412'),
    source: 'game-server',
    status: 'success',
    startedAt: ago(286_000),
    completedAt: ago(277_580),
    durationMs: 8420,
    tokenUsage: { input: 4150, output: 882, total: 5032 },
    headline: '酒馆线索询问 · 固定交互',
  },
  {
    traceId: opaque<TraceId>('trace_01JZNQZH2Q'),
    runId: opaque<RunId>('run_3d198cfe'),
    roomId: opaque<RoomId>('room-elm-11'),
    playerId: opaque<PlayerId>('player_0921'),
    source: 'ai-backend',
    status: 'running',
    startedAt: ago(421_000),
    tokenUsage: { input: 2890, output: 0, total: 2890 },
    headline: '市集巡逻 · 观看模式',
  },
  {
    traceId: opaque<TraceId>('trace_01JZNQRT3M'),
    runId: opaque<RunId>('run_5f4aa902'),
    roomId: opaque<RoomId>('room-cedar-02'),
    source: 'unity-client',
    status: 'success',
    startedAt: ago(715_000),
    completedAt: ago(708_269),
    durationMs: 6731,
    tokenUsage: { input: 3212, output: 694, total: 3906 },
    headline: '仓库潜入 · 观看模式',
  },
  {
    traceId: opaque<TraceId>('trace_01JZNQKC1A'),
    runId: opaque<RunId>('run_118d83ba'),
    roomId: opaque<RoomId>('room-cedar-02'),
    playerId: opaque<PlayerId>('player_6619'),
    source: 'ai-backend',
    status: 'success',
    startedAt: ago(1_052_000),
    completedAt: ago(1_042_072),
    durationMs: 9928,
    tokenUsage: { input: 5803, output: 1409, total: 7212 },
    headline: '船长室谈判 · 自由行动',
  },
];

export const mockModelRequests: ModelHttpRequestSummary[] = [
  {
    id: 135,
    observedAt: ago(18_000),
    runId: 'run_9c1fd83a',
    roomId,
    kind: 'metronome',
    status: 'success',
    durationMs: 3276,
    requestBytes: 15897,
    requestTruncated: false,
    responseChars: 292,
    toolCallNames: ['set_flag'],
    tokenUsage: { input: 4174, output: 292, total: 4466 },
  },
  {
    id: 134,
    observedAt: ago(74_000),
    runId: 'run_9c1fd83a',
    roomId,
    kind: 'main',
    status: 'error',
    error: 'Model gateway timeout',
    durationMs: 120000,
    requestBytes: 22104,
    requestTruncated: false,
    responseChars: 0,
    toolCallNames: [],
    tokenUsage: { input: 0, output: 0, total: 0 },
  },
];

export const mockModelRequestDetails: Record<number, ModelHttpRequestDetail> = {
  135: {
    ...mockModelRequests[0]!,
    requestBody: {
      model: 'deepseek-default-v4-flash',
      stream: true,
      messages: [
        { role: 'system', content: '你是叙事节拍器。' },
        { role: 'user', content: '检查当前世界状态并推进节拍。' },
      ],
      tools: [{ type: 'function', function: { name: 'set_flag' } }],
    },
    responseText: '节拍检查完成。',
    responseBody: {
      output_text: '节拍检查完成。',
      tool_calls: [{ name: 'set_flag', arguments: { key: 'scene_ready', value: true } }],
      usage: { input_tokens: 4174, output_tokens: 292, total_tokens: 4466 },
    },
    responseTruncated: false,
    toolCalls: [{ name: 'set_flag', arguments: { key: 'scene_ready', value: true } }],
  },
  134: {
    ...mockModelRequests[1]!,
    requestBody: { model: 'deepseek-default-v4-flash', messages: [] },
    responseBody: {
      tool_calls: [],
      usage: { input_tokens: 0, output_tokens: 0, total_tokens: 0 },
      error: 'Model gateway timeout',
    },
    responseTruncated: false,
    toolCalls: [],
  },
};

const event = (
  id: string,
  offsetMs: number,
  source: TraceEvent['source'],
  type: TraceEvent['type'],
  payload: TraceEvent['payload'],
): TraceEvent => ({
  schemaVersion: 1,
  eventId: opaque<EventId>(id),
  traceId,
  runId,
  roomId,
  playerId,
  source,
  type,
  sequence: Number(id.replace(/\D/g, '')) || 0,
  occurredAt: ago(92_000 - offsetMs),
  receivedAt: ago(91_970 - offsetMs),
  payload,
});

const aiRoundtrip: AiRoundtripDetail = {
  roundtripId: opaque<RoundtripId>('roundtrip-01'),
  traceId,
  runId,
  roomId,
  playerId,
  kind: 'main',
  provider: 'openai-compatible',
  model: 'claude-sonnet-4',
  status: 'success',
  startedAt: ago(91_720),
  completedAt: ago(85_382),
  durationMs: 6338,
  requestBytes: 48_210,
  tokenUsage: { input: 6380, output: 1234, total: 7614 },
  finishReason: 'tool_use',
  responsePreview: '海风把焦油与咸腥味一并推过码头。',
  toolCalls: [
    {
      callId: 'tool-01',
      name: 'move_actor',
      arguments: { actorId: 'guard_02', action: 'look_at_crate', durationMs: 1800 },
      result: { accepted: true, sequence: 42 },
      status: 'success',
      durationMs: 31,
    },
    {
      callId: 'tool-02',
      name: 'present_choices',
      arguments: { choices: ['继续观察', '返回人群', '自由行动'] },
      result: { accepted: true, sequence: 43 },
      status: 'success',
      durationMs: 18,
    },
  ],
  request: {
    system: '你是 Maland 世界中的叙事导演。严格交替输出旁白和表演动作。',
    messages: [
      { role: 'user', content: '我想绕到守卫背后观察箱子。' },
      { role: 'system', content: '当前模式：free_input；room=room-raven-04' },
    ],
    tools: [
      { name: 'move_actor', description: '移动或驱动场景演员表演' },
      { name: 'present_choices', description: '在可交互节点展示选项' },
    ],
  },
  response: {
    text: '海风把焦油与咸腥味一并推过码头。守卫的披风短暂遮住了视线。',
    finishReason: 'tool_use',
    toolCalls: [
      {
        callId: 'tool-01',
        name: 'move_actor',
        arguments: { actorId: 'guard_02', action: 'look_at_crate', durationMs: 1800 },
        result: { accepted: true, sequence: 42 },
        status: 'success',
        durationMs: 31,
      },
      {
        callId: 'tool-02',
        name: 'present_choices',
        arguments: { choices: ['继续观察', '返回人群', '自由行动'] },
        result: { accepted: true, sequence: 43 },
        status: 'success',
        durationMs: 18,
      },
    ],
  },
};

export const mockTraceDetails: Record<string, TraceDetail> = {
  [traceId]: {
    ...mockTraces[0]!,
    promptVersion: 'narrative-v18.4',
    gameVersion: 'v0.36.0',
    tags: ['playback-gap', 'free-input'],
    timeline: [
      event('evt-1', 0, 'game-server', 'run.started', { summary: '玩家自由输入：“我想绕到守卫背后观察箱子。”' }),
      event('evt-2', 280, 'ai-backend', 'ai.roundtrip.started', { summary: 'narrative-v18.4 · 19 messages · 12 tools' }),
      event('evt-3', 5852, 'ai-backend', 'narration.started', { text: '海风把焦油与咸腥味一并推过码头……' }),
      event('evt-4', 6670, 'game-server', 'performance.queued', { action: 'move_actor · guard_02 · look_at_crate' }),
      event('evt-5', 7125, 'unity-client', 'performance.started', { summary: '动作队列预计 1.8 s 完成' }),
      event('evt-6', 8160, 'unity-client', 'options.shown', { severity: 'error', summary: '表演动作仍有约 765 ms；期望等待全部旁白与动作释放完毕', expectedAfter: 'narrative_fully_released', actualMode: 'interact' }),
      event('evt-7', 9792, 'unity-client', 'performance.completed', { summary: '客户端表演动作完成' }),
      event('evt-8', 12_842, 'game-server', 'run.completed', { severity: 'error', summary: '运行结束（含时序警告）' }),
    ],
    aiRoundtrips: [aiRoundtrip],
  },
};

for (const trace of mockTraces.slice(1)) {
  mockTraceDetails[trace.traceId] = {
    ...trace,
    promptVersion: 'narrative-v18.4',
    gameVersion: 'v0.36.0',
    timeline: [
      {
        schemaVersion: 1,
        eventId: opaque<EventId>(`${trace.traceId}-start`),
        traceId: trace.traceId,
        runId: trace.runId,
        roomId: trace.roomId,
        ...(trace.playerId ? { playerId: trace.playerId } : {}),
        source: trace.source,
        type: 'run.started',
        sequence: 0,
        occurredAt: trace.startedAt,
        payload: { summary: trace.headline },
      },
      {
        schemaVersion: 1,
        eventId: opaque<EventId>(`${trace.traceId}-end`),
        traceId: trace.traceId,
        runId: trace.runId,
        roomId: trace.roomId,
        ...(trace.playerId ? { playerId: trace.playerId } : {}),
        source: trace.source,
        type: trace.status === 'running' ? 'system.notice' : 'run.completed',
        sequence: 1,
        occurredAt: trace.completedAt ?? ago(419_000),
        payload: trace.status === 'running' ? { status: 'running', summary: '等待后续事件' } : { summary: '运行完成' },
      },
    ],
    aiRoundtrips: [],
  };
}

const viewer: ViewerIdentity = {
  subject: 'cf-access|forrest',
  email: 'forrest@maland.internal',
  displayName: 'Forrest',
  role: 'admin',
  permissions: ['health:read', 'trace:read', 'ai-detail:read', 'trace:export', 'trace:retry', 'audit:read', 'room:close', 'player:kick', 'config:write', 'access:admin'],
  authenticatedAt: ago(1_800_000),
};

const checkedAt = ago(3_000);
const status: PlatformStatus = {
  environment: 'closed-beta',
  release: 'platform-0.1.0',
  state: 'degraded',
  checkedAt,
  components: [
    { id: 'game', name: 'Game Server', state: 'healthy', version: 'v0.36.0', checkedAt, latencyMs: 34 },
    { id: 'ai', name: 'AI Backend', state: 'healthy', version: 'v0.4.1', checkedAt, latencyMs: 82 },
    { id: 'trace', name: 'Trace Pipeline', state: 'degraded', version: 'v0.1.0', checkedAt, latencyMs: 147, message: 'Unity 客户端事件覆盖率 86%' },
  ],
};

export const mockDashboard: DashboardData = {
  viewer,
  status,
  traces: { items: mockTraces, total: mockTraces.length },
};

export const mockAuditEvents: AuditEventDto[] = [
  {
    type: 'gm_api.audit',
    occurredAt: ago(24_000),
    requestId: 'req_01JZNS1H4A',
    traceId: '9ee302cc78264d8395eeddd37552d2de',
    actor: { subject: 'cf-access|forrest', email: 'forrest@maland.internal', role: 'admin' },
    action: 'trace.read',
    resource: 'trace:ops-1842',
    outcome: 'succeeded',
    status: 200,
    durationMs: 128,
  },
  {
    type: 'gm_api.audit',
    occurredAt: ago(78_000),
    requestId: 'req_01JZNRX7QW',
    traceId: 'd8e65244a4d54f21a27a87fc5d156891',
    actor: { subject: 'cf-access|qa-04', email: 'qa-04@maland.internal', role: 'viewer' },
    action: 'trace.list',
    resource: 'trace:*',
    outcome: 'succeeded',
    status: 200,
    durationMs: 84,
  },
  {
    type: 'gm_api.audit',
    occurredAt: ago(142_000),
    requestId: 'req_01JZNRP2K8',
    traceId: 'ff6ec2867fcb4bf4a576e5b0af23fba2',
    actor: { subject: 'cf-access|operator-02', email: 'operator-02@maland.internal', role: 'operator' },
    action: 'audit.read',
    resource: 'audit:*',
    outcome: 'rejected',
    status: 403,
    durationMs: 16,
  },
  {
    type: 'gm_api.audit',
    occurredAt: ago(306_000),
    requestId: 'req_01JZNRB93N',
    traceId: '1bc1eed459754ea1825ca1df6ee20a82',
    actor: null,
    action: 'authentication.reject',
    resource: '/api/traces',
    outcome: 'rejected',
    status: 401,
    durationMs: 9,
  },
];
